/**
 * Recents in Home's sidebar: T3 Code's thread list on Home, with
 * its multi-select (mod-click, shift-click, Esc) and bulk menu, mod+1..9 and
 * previous/next chat, and "Show more" paging. It is a multi-select tree:
 * chats at level 1, the Snoozed and Settled shelves as expandable level-1
 * items with their chats at level 2, one tab stop, and arrows, Home and End
 * to move. The model is HomeSidebarRecents.logic.
 *
 * @module components/home/HomeSidebarRecents
 */
import { useAtomValue } from "@effect/atom-react";
import { scopeThreadRef, scopedThreadKey } from "@amoreg/client-runtime/environment";
import { effectiveSettled, effectiveSnoozed } from "@amoreg/client-runtime/state/thread-settled";
import type { ContextMenuPosition, ScopedThreadRef, ThreadId } from "@amoreg/contracts";
import { useLocation, useParams } from "@tanstack/react-router";
import * as Schema from "effect/Schema";
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { isMacPlatform } from "~/lib/utils";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { useNowMinute } from "../../hooks/useNowMinute";
import { useClientSettings } from "../../hooks/useSettings";
import { deriveProviderInstanceEntries } from "../../providerInstances";
import { useAllEnvironmentShellsBootstrapped, useAnyEnvironmentSnapshot, useThreadShells } from "../../state/entities";
import { usePrimaryEnvironmentId } from "../../state/environments";
import { useActiveOrgId } from "../../state/orgs";
import { environmentServerConfigsAtom, primaryServerKeybindingsAtom, primaryServerProvidersAtom } from "../../state/server";
import { spaceByThreadId, useOrgSpaces } from "../../state/spaces";
import { selectThreadTerminalUiState, useTerminalUiStateStore } from "../../terminalUiStateStore";
import { useThreadSelectionStore } from "../../threadSelectionStore";
import {
  buildRecentsModel,
  RECENTS_FIRST_PAGE,
  RECENTS_PAGE_COUNT,
  recentsEmptyState,
  recentsItemKey,
  recentsRowKey,
  recentsShortcutCommand,
  recentsTabStop,
  SETTLED_PREVIEW_COUNT,
  shortcutTargetRow,
  type RecentsItem,
  type RecentsRow,
} from "./HomeSidebarRecents.logic";
import { Skeleton } from "../ui/skeleton";
import { selectOrgThreads } from "./homeLogic";
import { RecentsShelfHeader } from "./RecentsShelfHeader";
import { RecentsShowMore } from "./RecentsShowMore";
import { RecentThreadRow } from "./RecentThreadRow";
import { useRecentThreadActions } from "./useRecentThreadActions";

const TREE_ITEM_SELECTOR = '[role="treeitem"]';

export function HomeSidebarRecents({
  query,
  onNavigate,
}: {
  /** The Recents filter; empty shows everything. */
  readonly query: string;
  readonly onNavigate: () => void;
}) {
  const orgId = useActiveOrgId();
  const environmentId = usePrimaryEnvironmentId();
  const threads = useThreadShells();
  // An empty list is only "No chats yet" once the shells bootstrapped and some
  // environment answered; until then (loading, or every environment
  // disconnected) it is "not here yet", drawn as placeholder rows.
  const shellsBootstrapped = useAllEnvironmentShellsBootstrapped();
  const anySnapshot = useAnyEnvironmentSnapshot();
  const threadsKnown = shellsBootstrapped && anySnapshot;
  const serverConfigs = useAtomValue(environmentServerConfigsAtom);
  const autoSettleAfterDays = useClientSettings((settings) => settings.sidebarAutoSettleAfterDays);
  const timestampFormat = useClientSettings((settings) => settings.timestampFormat);
  const keybindings = useAtomValue(primaryServerKeybindingsAtom);
  const serverProviders = useAtomValue(primaryServerProvidersAtom);
  const nowMinute = useNowMinute();
  const [snoozedOpen, setSnoozedOpen] = useLocalStorage("home_sidebar_recents_snoozed_open", false, Schema.Boolean);
  const [settledOpen, setSettledOpen] = useLocalStorage("home_sidebar_recents_settled_open", true, Schema.Boolean);
  const [settledVisible, setSettledVisible] = useState(SETTLED_PREVIEW_COUNT);
  const [limit, setLimit] = useState(RECENTS_FIRST_PAGE);
  const routeThreadId = useParams({
    strict: false,
    select: (params: Partial<Record<string, string>>) => params.threadId ?? null,
  });
  const routePath = useLocation({ select: (location) => location.pathname });

  const model = useMemo(() => {
    const now = new Date().toISOString();
    const minuteNow = `${nowMinute}:00.000Z`;
    return buildRecentsModel({
      threads: selectOrgThreads(threads, { orgId, environmentId }),
      shelfOf: (thread) => {
        const capabilities = serverConfigs.get(thread.environmentId)?.environment.capabilities;
        if (capabilities?.threadSnooze === true && effectiveSnoozed(thread, { now })) return "snoozed";
        if (
          capabilities?.threadSettlement === true &&
          effectiveSettled(thread, { now: minuteNow, autoSettleAfterDays, changeRequestState: null })
        )
          return "settled";
        return null;
      },
      limit,
      settledVisible,
      snoozedOpen,
      settledOpen,
      routeThreadId,
      query,
    });
  }, [autoSettleAfterDays, environmentId, limit, nowMinute, orgId, query, routeThreadId, serverConfigs, settledOpen, settledVisible, snoozedOpen, threads]);

  const providerEntries = useMemo(
    () => new Map(deriveProviderInstanceEntries(serverProviders).map((entry) => [entry.instanceId as string, entry] as const)),
    [serverProviders],
  );
  const orgSpaces = useOrgSpaces(orgId);
  const threadSpaces = useMemo(
    () => spaceByThreadId(orgSpaces.spaces, orgSpaces.spaceIdByThreadId),
    [orgSpaces.spaces, orgSpaces.spaceIdByThreadId],
  );

  const rowByKey = useMemo(() => new Map(model.rows.map((row) => [recentsRowKey(row.thread), row] as const)), [model.rows]);
  const rowByKeyRef = useRef<ReadonlyMap<string, RecentsRow>>(rowByKey);
  rowByKeyRef.current = rowByKey;
  const { handlers, renaming, openThreadMenu, openSelectionMenu } = useRecentThreadActions({
    onNavigate,
    rowByKey: rowByKeyRef,
  });

  // Multi-select, as T3 Code's thread sidebar (Sidebar.tsx handleThreadClick).
  const clearSelection = useThreadSelectionStore((state) => state.clearSelection);
  const setSelectionAnchor = useThreadSelectionStore((state) => state.setAnchor);
  const toggleThreadSelection = useThreadSelectionStore((state) => state.toggleThread);
  const rangeSelectTo = useThreadSelectionStore((state) => state.rangeSelectTo);
  const selectionSize = useThreadSelectionStore((state) => state.selectedThreadKeys.size);

  useEffect(() => {
    clearSelection();
  }, [clearSelection, orgId]);

  useEffect(() => {
    if (selectionSize === 0) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.key !== "Escape") return;
      event.preventDefault();
      clearSelection();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [clearSelection, selectionSize]);

  const onRowClick = useCallback(
    (event: ReactMouseEvent, ref: ScopedThreadRef, projectId: string) => {
      const threadKey = scopedThreadKey(ref);
      const isModClick = isMacPlatform(navigator.platform) ? event.metaKey : event.ctrlKey;
      if (isModClick) {
        event.preventDefault();
        toggleThreadSelection(threadKey);
        return;
      }
      if (event.shiftKey) {
        event.preventDefault();
        rangeSelectTo(threadKey, [...rowByKeyRef.current.keys()]);
        return;
      }
      if (event.detail > 1) return;
      if (useThreadSelectionStore.getState().selectedThreadKeys.size > 0) clearSelection();
      setSelectionAnchor(threadKey);
      handlers.open(ref, projectId);
    },
    [clearSelection, handlers, rangeSelectTo, setSelectionAnchor, toggleThreadSelection],
  );

  const onRowContextMenu = useCallback(
    (threadKey: string, position: ContextMenuPosition) => {
      const selection = useThreadSelectionStore.getState();
      if (selection.hasSelection() && selection.selectedThreadKeys.has(threadKey)) {
        void openSelectionMenu(position);
        return;
      }
      const row = rowByKeyRef.current.get(threadKey);
      if (row) void openThreadMenu(row.thread, row.section, position);
    },
    [openSelectionMenu, openThreadMenu],
  );

  const listRef = useRef<HTMLUListElement | null>(null);
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const onListKeyDown = (event: ReactKeyboardEvent<HTMLUListElement>) => {
    const target = event.target as HTMLElement;
    if (target.tagName === "INPUT") return;
    const expanded = target.getAttribute("role") === "treeitem" ? target.getAttribute("aria-expanded") : null;
    if ((event.key === "ArrowRight" && expanded === "false") || (event.key === "ArrowLeft" && expanded === "true")) {
      event.preventDefault();
      target.click();
      return;
    }
    const items = Array.from(listRef.current?.querySelectorAll<HTMLElement>(TREE_ITEM_SELECTOR) ?? []);
    const index = items.findIndex((item) => item === document.activeElement);
    const move = (next: number) => {
      event.preventDefault();
      items[Math.max(0, Math.min(items.length - 1, next))]?.focus();
    };
    if (event.key === "ArrowDown") move(index + 1);
    else if (event.key === "ArrowUp") move(index - 1);
    else if (event.key === "Home") move(0);
    else if (event.key === "End") move(items.length - 1);
  };

  // mod+1..9 opens the Nth row; previous and next chat walk the rows.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.repeat) return;
      const routeThreadRef =
        routeThreadId && environmentId ? scopeThreadRef(environmentId, routeThreadId as ThreadId) : null;
      const command = recentsShortcutCommand(event, keybindings, {
        platform: navigator.platform,
        terminalOpen: selectThreadTerminalUiState(
          useTerminalUiStateStore.getState().terminalUiStateByThreadKey,
          routeThreadRef,
        ).terminalOpen,
      });
      const target = shortcutTargetRow(model.rows, command, routePath);
      if (!target) return;
      event.preventDefault();
      event.stopPropagation();
      handlers.open(scopeThreadRef(target.thread.environmentId, target.thread.id), target.thread.projectId);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [environmentId, handlers, keybindings, model.rows, routePath, routeThreadId]);

  const itemKeys = useMemo(() => model.items.map(recentsItemKey), [model.items]);
  const tabStop = recentsTabStop(itemKeys, focusKey);

  const renderRow = (row: RecentsRow, index: number, key: string) => {
    const { thread, section } = row;
    const caps = serverConfigs.get(thread.environmentId)?.environment.capabilities;
    return (
      <RecentThreadRow
        key={key}
        thread={thread}
        section={section}
        index={index}
        space={threadSpaces.get(thread.id) ?? null}
        providerEntry={providerEntries.get(thread.session?.providerInstanceId ?? thread.modelSelection.instanceId) ?? null}
        settlementSupported={caps?.threadSettlement === true}
        snoozeSupported={caps?.threadSnooze === true}
        pinningSupported={caps?.threadPinning === true}
        timestampFormat={timestampFormat}
        isRouteThread={routeThreadId === thread.id}
        isRenaming={renaming.key === key}
        renamingTitle={renaming.key === key ? renaming.title : ""}
        onRenameTitleChange={renaming.setTitle}
        tabIndex={tabStop === key ? 0 : -1}
        onFocusRow={setFocusKey}
        handlers={handlers}
        highlight={query}
        onRowClick={onRowClick}
        onContextMenu={onRowContextMenu}
      />
    );
  };

  const renderItem = (item: RecentsItem, key: string) => {
    if (item.kind === "row") return renderRow(item.row, item.index, key);
    const focus = { tabIndex: tabStop === key ? 0 : -1, onFocus: () => setFocusKey(key) };
    if (item.kind === "shelf") {
      const toggle = item.shelf === "snoozed" ? () => setSnoozedOpen(!snoozedOpen) : () => setSettledOpen(!settledOpen);
      return (
        <RecentsShelfHeader
          key={key}
          label={item.shelf === "snoozed" ? "Snoozed" : "Settled"}
          count={item.count}
          open={item.open}
          filtering={model.filtering}
          onToggle={toggle}
          {...focus}
        />
      );
    }
    return (
      <RecentsShowMore
        key={key}
        level={item.under === "active" ? 1 : 2}
        count={item.count}
        onClick={
          item.under === "active"
            ? () => setLimit((count) => count + RECENTS_PAGE_COUNT)
            : () => setSettledVisible((count) => count + RECENTS_PAGE_COUNT)
        }
        {...focus}
      />
    );
  };

  const empty = model.items.length === 0;

  if (recentsEmptyState({ empty, filtering: model.filtering, threadsKnown }) === "loading") {
    return (
      <ul aria-busy="true" aria-label="Loading chats" className="flex flex-col gap-0.5 ps-px" data-home-recents="" data-recents-loading="">
        {[0, 1, 2].map((row) => (
          <li key={row} className="flex h-8 list-none items-center gap-2 px-2.5">
            <Skeleton className="size-4 shrink-0 rounded-sm" />
            <Skeleton className={row === 1 ? "h-3 w-24 rounded" : "h-3 w-32 rounded"} />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <ul
      ref={listRef}
      role={empty ? undefined : "tree"}
      aria-multiselectable={empty ? undefined : true}
      aria-label="Recent chats"
      onKeyDown={onListKeyDown}
      className="relative flex flex-col gap-0.5 ps-px"
      data-home-recents=""
    >
      {empty ? (
        <li
          data-recents-empty={model.filtering ? "no-match" : "none"}
          className="list-none px-2.5 py-1 text-xs text-sidebar-muted-foreground/70"
        >
          {model.filtering ? "No chats match" : "No chats yet"}
        </li>
      ) : null}
      {model.items.map((item, index) => renderItem(item, itemKeys[index]!))}
    </ul>
  );
}
